import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/tabon/Logo";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Info, ArrowLeft } from "lucide-react";

export default function StaffLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [venueCode, setVenueCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) {
      toast.error("PIN must be 4 digits");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff-login", { venue_code: venueCode, pin });
      setUser(data);
      toast.success(`Welcome ${data.name}`);
      const hasOrdering = data?.venue?.features?.ordering === true;
      navigate(hasOrdering ? "/orders" : "/billing");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 tb-hero-gradient">
        <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
        <div className="relative w-full max-w-md tb-fade-up">
          <div className="flex flex-col items-center mb-8">
            <Logo size="xl" />
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#9D00FF]/20 border border-[#9D00FF]/40 flex items-center justify-center">
                <Info size={18} className="text-[#9D00FF]" />
              </div>
              <h2 className="text-xl font-bold">Forgot Your PIN?</h2>
            </div>
            <div className="flex flex-col gap-3 text-sm text-[#A0A0A0]">
              <p>Ask your manager to reset your PIN in the venue dashboard.</p>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 text-xs space-y-1 text-white/80">
                <div className="font-bold text-white mb-2">How to reset a staff PIN:</div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#9D00FF]/30 text-[#9D00FF] font-bold text-[10px] flex items-center justify-center shrink-0">1</span>
                  Dashboard → <span className="text-[#9D00FF] font-semibold">Settings</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#9D00FF]/30 text-[#9D00FF] font-bold text-[10px] flex items-center justify-center shrink-0">2</span>
                  Go to <span className="text-[#9D00FF] font-semibold">Staff Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#9D00FF]/30 text-[#9D00FF] font-bold text-[10px] flex items-center justify-center shrink-0">3</span>
                  Find your name → click <span className="text-[#9D00FF] font-semibold">Reset PIN</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#9D00FF]/30 text-[#9D00FF] font-bold text-[10px] flex items-center justify-center shrink-0">4</span>
                  Your PIN will be reset to <span className="font-mono font-bold text-white">0000</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#9D00FF]/30 text-[#9D00FF] font-bold text-[10px] flex items-center justify-center shrink-0">5</span>
                  Log in with <span className="font-mono font-bold text-white">0000</span> and set a new PIN
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowForgot(false)}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg py-3 min-h-[44px] text-sm font-semibold text-white hover:bg-white/5 transition"
            >
              <ArrowLeft size={15} /> Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 tb-hero-gradient">
      <div className="absolute inset-0 tb-grid-bg opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md tb-fade-up">
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" />
          <p className="text-[#A0A0A0] mt-3 text-sm italic">Staff quick access</p>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
          <h1 className="text-2xl font-bold mb-1">Staff Login</h1>
          <p className="text-sm text-[#A0A0A0] mb-6">Enter your venue code and 4-digit PIN</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Venue Code
              </label>
              <input
                data-testid="staff-venue-code-input"
                value={venueCode}
                onChange={(e) => setVenueCode(e.target.value.toUpperCase())}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white text-center font-mono text-lg tracking-[0.3em] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                placeholder="Enter venue code"
                maxLength={12}
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                PIN
              </label>
              <input
                data-testid="staff-pin-input"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-5 text-white text-center text-4xl font-bold tracking-[0.5em] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                placeholder="••••"
                maxLength={4}
                required
              />
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="staff-forgot-pin"
                className="text-xs text-[#A0A0A0] hover:text-white mt-2 underline underline-offset-2 transition block mx-auto"
              >
                Forgot PIN?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading || pin.length !== 4}
              data-testid="staff-login-submit-button"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all active:scale-[0.99]"
            >
              {loading ? "Signing in…" : "Enter"}
            </button>
          </form>

          <div className="text-center text-sm text-[#A0A0A0] mt-5">
            Admin login?{" "}
            <Link to="/login" className="text-[#9D00FF] font-semibold hover:underline">
              Sign in here
            </Link>
          </div>
        </div>
        <div className="text-center text-xs text-[#A0A0A0] mt-6">
          Powered by <span className="text-white">TabOn</span> · tabon.app
        </div>
      </div>
    </div>
  );
}
